import { atom } from 'jotai';

import type { Facility, Organization } from '@crossnokaye/typescript-sdk/models/components';

import {
  resolveAgentId,
  resolveFacilityId,
  resolveOrganizationId,
} from '@/utils/facility-resolver';

import RouteState from './route.state';
import UserStateAtoms from './user.state';

const CurrentFacilityAtoms = {
  /**
   * Derived atom that resolves the current organization ID from userState and route params.
   * Uses shared resolution logic from facility-resolver utility.
   */
  organizationId: atom((get): string | undefined => {
    const userState = get(UserStateAtoms.user);
    const params = get(RouteState.route);
    return resolveOrganizationId(params, userState);
  }),

  /**
   * Derived atom that resolves the current organization from userState.
   */
  organization: atom((get): Organization | undefined => {
    const userState = get(UserStateAtoms.user);
    const organizationId = get(CurrentFacilityAtoms.organizationId);

    return organizationId ? userState?.organizations?.byId[organizationId] : undefined;
  }),

  /**
   * Derived atom that resolves the current facility ID from userState and route params.
   * Uses shared resolution logic from facility-resolver utility.
   */
  facilityId: atom((get): string | undefined => {
    const userState = get(UserStateAtoms.user);
    const params = get(RouteState.route);
    const organizationId = get(CurrentFacilityAtoms.organizationId);
    return resolveFacilityId(params, organizationId, userState);
  }),

  /**
   * Derived atom that resolves the current facility from userState.
   */
  facility: atom((get): Facility | undefined => {
    const userState = get(UserStateAtoms.user);
    const facilityId = get(CurrentFacilityAtoms.facilityId);

    return facilityId ? userState?.facilities?.byId[facilityId] : undefined;
  }),

  /**
   * Derived atom that resolves the current agent ID from the facility.
   * Uses shared resolution logic from facility-resolver utility.
   */
  agentId: atom((get): string | undefined => {
    const userState = get(UserStateAtoms.user);
    const facilityId = get(CurrentFacilityAtoms.facilityId);
    return resolveAgentId(facilityId, userState);
  }),
};

CurrentFacilityAtoms.organizationId.debugLabel = 'CurrentFacilityAtoms.OrganizationId';
CurrentFacilityAtoms.organization.debugLabel = 'CurrentFacilityAtoms.Organization';
CurrentFacilityAtoms.facilityId.debugLabel = 'CurrentFacilityAtoms.FacilityId';
CurrentFacilityAtoms.facility.debugLabel = 'CurrentFacilityAtoms.Facility';
CurrentFacilityAtoms.agentId.debugLabel = 'CurrentFacilityAtoms.AgentId';

export default CurrentFacilityAtoms;
